import { forwardRef, useCallback, useEffect, useId, useRef, type CSSProperties, type HTMLAttributes, type ReactNode, type RefObject } from 'react';
import { cn } from '../../utils/cn';
import { useFloating, usePresence, type Placement, type UseFloatingOptions } from '../../hooks/useFloating';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useMergedRefs } from '../Tooltip/mergeRefs';
import { Portal } from '../Portal/Portal';

export type { Placement };

const elevations = { 1: 'shadow-1', 2: 'shadow-2', 3: 'shadow-3', 4: 'shadow-4' } as const;
const surfaces = { 1: 'bg-surface-1', 2: 'bg-surface-2', 3: 'bg-surface-3' } as const;

export interface FloatingPanelProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    Omit<UseFloatingOptions, 'arrowRef' | 'zIndex'> {
  /** Element the panel is anchored to. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  children: ReactNode;
  /** Called when the panel should dismiss itself (outside pointer-down, Escape). */
  onClose?: () => void;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  /** Extra refs that count as "inside" for outside-click dismissal. */
  ignoreRefs?: Array<RefObject<HTMLElement | null>>;
  /** Draw a collision-aware arrow pointing at the anchor. */
  arrow?: boolean;
  elevation?: 1 | 2 | 3 | 4;
  surface?: 1 | 2 | 3;
  /** Portal target; defaults to `document.body`. */
  container?: HTMLElement | null;
  zIndex?: number | string;
  /** Rounded corner scale (popovers use `lg`). */
  radius?: 'md' | 'lg' | 'xl';
}

/**
 * The portalled, positioned, animated surface every Zagru overlay renders into.
 *
 * It stays mounted for the length of its exit animation, so overlays fade *out* instead of
 * vanishing; when motion is disabled (reduced-motion, or a test environment with no stylesheet)
 * the unmount is synchronous.
 */
/**
 * Which panel each open floating layer was opened from, by layer id.
 *
 * Module-level because the relationship is between two portals that never share a React subtree: the
 * listbox of a `Select` rendered inside a `Popover` has no way to reach that popover through context.
 */
const floatingOwners = new Map<string, string | null>();

export const FloatingPanel = forwardRef<HTMLDivElement, FloatingPanelProps>(function FloatingPanel(
  {
    anchorRef,
    open,
    children,
    onClose,
    closeOnOutsideClick = true,
    closeOnEscape = true,
    ignoreRefs,
    arrow = false,
    elevation = 3,
    surface = 2,
    container,
    zIndex = 'var(--zo-z-popover)',
    radius = 'lg',
    className,
    style,
    placement = 'bottom-start',
    offset = 6,
    padding = 8,
    flip = true,
    shift = true,
    matchWidth,
    matchWidthExact,
    size,
    arrowPadding,
    ...rest
  },
  ref,
) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLSpanElement | null>(null);
  const { mounted, state } = usePresence(open);
  const floating = useFloating(anchorRef, panelRef, mounted, {
    placement,
    offset,
    padding,
    flip,
    shift,
    matchWidth,
    matchWidthExact,
    size,
    arrowPadding,
    arrowRef: arrow ? arrowRef : undefined,
    zIndex,
  });

  /*
   * A layer opened *from* this one is not "outside" it.
   *
   * Every overlay portals to the body, so a `Select` inside a `Popover` is a sibling of it in the DOM and
   * the popover's outside-click handler saw the listbox as somewhere else entirely — choosing a value in
   * the filter builder shut the whole builder. Nesting is real even when the DOM does not show it, so it
   * is recorded: each panel remembers the panel its anchor sits in, and a pointer-down is inside this one
   * when it lands in any panel whose chain of owners leads back here.
   */
  const layerId = useId();
  const owner = anchorRef.current?.closest('[data-zo-floating-id]')?.getAttribute('data-zo-floating-id') ?? null;
  useEffect(() => {
    if (!mounted) return undefined;
    floatingOwners.set(layerId, owner);
    return () => {
      floatingOwners.delete(layerId);
    };
  }, [mounted, layerId, owner]);

  const isInsideALayerOfMine = useCallback(
    (target: Node) => {
      const panel = (target instanceof Element ? target : target.parentElement)?.closest('[data-zo-floating-id]');
      let id = panel?.getAttribute('data-zo-floating-id') ?? null;
      // Walk up the ownership chain; a cycle cannot form (a panel's owner already existed when it opened).
      while (id) {
        if (id === layerId) return true;
        id = floatingOwners.get(id) ?? null;
      }
      return false;
    },
    [layerId],
  );

  const dismissOnOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && isInsideALayerOfMine(target)) return;
      onClose?.();
    },
    [onClose, isInsideALayerOfMine],
  );
  const dismiss = useCallback(() => onClose?.(), [onClose]);
  useOutsideClick([anchorRef, panelRef, ...(ignoreRefs ?? [])], dismissOnOutside, open && closeOnOutsideClick && !!onClose);
  useEscapeKey(dismiss, open && closeOnEscape && !!onClose);
  // Stable identity: consumers hang open-time work (autofocus, focus traps, measurement) off this ref,
  // and a ref rebuilt per render would re-run that work on every unrelated render of the page.
  const setPanel = useMergedRefs<HTMLDivElement>(panelRef, ref);

  if (!mounted) return null;

  // `lg` is the overlay radius (10px): popovers, menus and listboxes sit one step rounder than cards.
  const radii = { md: 'rounded-md', lg: 'rounded-overlay', xl: 'rounded-xl' } as const;

  return (
    <Portal container={container}>
      <div
        ref={setPanel}
        data-state={state}
        data-placement={floating.placement}
        data-side={floating.side}
        data-zo-floating=""
        data-zo-floating-id={layerId}
        style={{ ...floating.style, ...style } as CSSProperties}
        className={cn(
          'zo-floating',
          radii[radius],
          surfaces[surface],
          elevations[elevation],
          'text-fg outline-none',
          className,
        )}
        {...rest}
      >
        {arrow ? (
          <span
            ref={arrowRef}
            aria-hidden
            data-zo-arrow=""
            style={floating.arrowStyle}
            className={cn('absolute size-2 rotate-45', surfaces[surface], 'shadow-1')}
          />
        ) : null}
        {children}
      </div>
    </Portal>
  );
});
